import { keepPreviousData } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import { functionalUpdate } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { getSearchResponseResponseSuccess } from '#/api/generated/search/search';
import { useGetSearchResponse } from '#/api/generated/search/search';
import { equityRowId } from '#/components/index/EquitiesExploreTable/data';
import { etfRowId } from '#/components/index/ETFsExploreTable/data';
import { mutualFundRowId } from '#/components/index/MutualFundsExploreTable/data';
import { CategoryNavigation } from '#/components/layout/CategoryNavigation';
import { ContentLayout } from '#/components/layout/ContentLayout';
import { DevSpacer } from '#/components/layout/DevSpacer';
import {
  getSearchEquityColumns,
  getSearchEtfColumns,
  getSearchMutualFundColumns,
} from '#/components/search/data';
import type {
  SearchExchangeCode,
  SearchMarketCapCategory,
} from '#/components/search/facetOptions.ts';
import {
  isSearchExchangeCode,
  isSearchMarketCapCategory,
} from '#/components/search/facetOptions.ts';
import { InvestmentTypeFilter } from '#/components/search/InvestmentTypeFilter';
import { SearchFacetFilters } from '#/components/search/SearchFacetFilters.tsx';
import type { SearchListingType } from '#/components/search/searchFacetFilters.types';
import {
  buildSearchState,
  getPaginationParams,
  getPaginationTokens,
  getSearchSortBy,
  getSearchSorting,
  getSearchSortParams,
  toSearchInvestmentType,
  toSearchOrderBy,
} from '#/components/search/searchPagination';
import { SearchResultsSection } from '#/components/search/SearchResultsSection';
import { TopResultCard } from '#/components/search/TopResultCard';
import { Icon } from '#/components/ui/icons';
import { QueryBoundary } from '#/components/ui/QueryBoundary';
import type { ReturnsTimeframeId } from '#/components/ui/returnsTimeframe';
import {
  getReturnsTimeframeFromSorting,
  syncSortingToReturnsTimeframe,
} from '#/components/ui/returnsTimeframe';
import { ReturnsTimeframeFilter } from '#/components/ui/ReturnsTimeframeFilter';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { Spinner } from '#/components/ui/Spinner';
import {
  INTERIOR_NAV_CTA,
  INTERIOR_NAV_PRODUCT_TYPES,
  INTERIOR_NAV_SECTORS,
} from '#/lib/category-navigation';
import { cn } from '#/utils/cn';

const SEARCH_RESULT_COUNT = 5;

const SEARCH_RETURN_COLUMN_IDS = {
  equity: {
    oneYear: 'trailing_1yr_return',
    fiveYear: 'trailing_5yr_return',
  },
  etf: {
    oneYear: 'trailing_1yr_return',
    fiveYear: 'return_5Y_annualized',
  },
  mutual_fund: {
    oneYear: 'trailing_1yr_return',
    fiveYear: 'return_5Y_annualized',
  },
} as const;

function getReturnSortByForTimeframe(timeframe: ReturnsTimeframeId) {
  return timeframe === '1Y' ? 'return_1y' : 'return_5y';
}

export const Route = createFileRoute('/search')({
  component: SearchPage,
  validateSearch: (search: Record<string, unknown>) => ({
    query: typeof search.query === 'string' ? search.query : '',
    investment_type:
      typeof search.investment_type === 'string'
        ? search.investment_type
        : undefined,
    sort_by: typeof search.sort_by === 'string' ? search.sort_by : undefined,
    order_by: typeof search.order_by === 'string' ? search.order_by : undefined,
    equity_sector_slug: toSearchFilterValues(search.equity_sector_slug),
    equity_industry_slug: toSearchFilterValues(search.equity_industry_slug),
    market_cap_categories: toSearchMarketCapCategories(
      toSearchFilterValues(search.market_cap_categories),
    ),
    fund_category_slug: toSearchFilterValues(search.fund_category_slug),
    equity_next_pagination_token:
      typeof search.equity_next_pagination_token === 'string'
        ? search.equity_next_pagination_token
        : undefined,
    equity_prev_pagination_token:
      typeof search.equity_prev_pagination_token === 'string'
        ? search.equity_prev_pagination_token
        : undefined,
    etf_next_pagination_token:
      typeof search.etf_next_pagination_token === 'string'
        ? search.etf_next_pagination_token
        : undefined,
    etf_prev_pagination_token:
      typeof search.etf_prev_pagination_token === 'string'
        ? search.etf_prev_pagination_token
        : undefined,
    mutual_fund_next_pagination_token:
      typeof search.mutual_fund_next_pagination_token === 'string'
        ? search.mutual_fund_next_pagination_token
        : undefined,
    mutual_fund_prev_pagination_token:
      typeof search.mutual_fund_prev_pagination_token === 'string'
        ? search.mutual_fund_prev_pagination_token
        : undefined,
    listing_type:
      typeof search.listing_type === 'string' ? search.listing_type : undefined,
    exchange: toSearchExchangeCodes(toSearchFilterValues(search.exchange)),
  }),
});

function SearchPage() {
  const search = Route.useSearch();
  const investmentType = toSearchInvestmentType(search.investment_type);
  const sortBy = getSearchSortBy(investmentType, search.sort_by);
  const orderBy = sortBy ? toSearchOrderBy(search.order_by) : undefined;
  const searchSorting = getSearchSorting(investmentType, sortBy, orderBy);
  const equitySectorSlugs =
    investmentType === 'equity' ? search.equity_sector_slug : undefined;
  const equityIndustrySlugs =
    investmentType === 'equity' ? search.equity_industry_slug : undefined;
  const marketCapCategories =
    investmentType === 'equity' ? search.market_cap_categories : undefined;
  const fundCategorySlugs =
    investmentType === 'etf' || investmentType === 'mutual_fund'
      ? search.fund_category_slug
      : undefined;
  const listingType =
    investmentType === 'equity' || investmentType === 'etf'
      ? toSearchListingType(search.listing_type)
      : undefined;
  const exchangeCodes =
    investmentType === 'equity' || investmentType === 'etf'
      ? search.exchange
      : undefined;
  const { query } = search;
  const navigate = Route.useNavigate();
  const [resultsTimeframe, setResultsTimeframe] = useState<ReturnsTimeframeId>(
    () => (sortBy === 'return_1y' ? '1Y' : '5Y'),
  );
  // Live-region message announced to screen readers after a Next/Prev click
  // settles, so non-visual users learn the results table has been replaced.
  const [paginationAnnouncement, setPaginationAnnouncement] = useState('');
  // Remembers which pagination direction was clicked while the next page is
  // still loading; read once fetching settles, then cleared.
  const pendingPaginationDirectionRef = useRef<'next' | 'prev' | null>(null);
  const returnColumnIds =
    investmentType != null ? SEARCH_RETURN_COLUMN_IDS[investmentType] : null;

  useEffect(() => {
    if (!returnColumnIds) {
      return;
    }

    const nextTimeframe = getReturnsTimeframeFromSorting(
      searchSorting,
      returnColumnIds,
      resultsTimeframe,
    );

    if (nextTimeframe !== resultsTimeframe) {
      setResultsTimeframe(nextTimeframe);
    }
  }, [resultsTimeframe, returnColumnIds, searchSorting]);

  const showAll = !investmentType;
  const isResultsSortable = !showAll;
  const { equityColumns, etfColumns, mutualFundColumns } = useMemo(
    () => ({
      equityColumns: getSearchEquityColumns(
        resultsTimeframe,
        isResultsSortable,
      ),
      etfColumns: getSearchEtfColumns(resultsTimeframe, isResultsSortable),
      mutualFundColumns: getSearchMutualFundColumns(
        resultsTimeframe,
        isResultsSortable,
      ),
    }),
    [isResultsSortable, resultsTimeframe],
  );

  function handlePaginationChange(direction: 'next' | 'prev', cursor?: string) {
    if (!investmentType || !cursor) {
      return;
    }

    // Record the direction so the live-region effect can announce the result
    // once the new page finishes loading.
    pendingPaginationDirectionRef.current = direction;

    void navigate({
      search: buildSearchState({
        query,
        investment_type: investmentType,
        sort_by: sortBy,
        order_by: orderBy,
        equity_sector_slug: equitySectorSlugs,
        equity_industry_slug: equityIndustrySlugs,
        market_cap_categories: marketCapCategories,
        fund_category_slug: fundCategorySlugs,
        listing_type: listingType,
        exchange: exchangeCodes,
        ...getPaginationParams(investmentType, direction, cursor),
      }),
      resetScroll: false,
    });
  }

  function handleInvestmentTypeChange(value: typeof investmentType) {
    const nextSortBy = getSearchSortBy(value, sortBy);

    void navigate({
      search: buildSearchState({
        query,
        investment_type: value,
        sort_by:
          value && !nextSortBy
            ? getReturnSortByForTimeframe(resultsTimeframe)
            : nextSortBy,
        order_by:
          value && !nextSortBy
            ? (orderBy ?? 'desc')
            : nextSortBy
              ? orderBy
              : undefined,
        equity_sector_slug:
          value === 'equity' ? search.equity_sector_slug : undefined,
        equity_industry_slug:
          value === 'equity' ? search.equity_industry_slug : undefined,
        market_cap_categories:
          value === 'equity' ? search.market_cap_categories : undefined,
        fund_category_slug:
          value === 'etf' || value === 'mutual_fund'
            ? search.fund_category_slug
            : undefined,
        listing_type:
          value === 'equity' || value === 'etf'
            ? search.exchange?.length
              ? undefined
              : toSearchListingType(search.listing_type)
            : undefined,
        exchange:
          value === 'equity' || value === 'etf' ? search.exchange : undefined,
      }),
      resetScroll: false,
    });
  }

  function handleFacetChange(next: {
    equity_sector_slug?: string[];
    equity_industry_slug?: string[];
    market_cap_categories?: SearchMarketCapCategory[];
    fund_category_slug?: string[];
    listing_type?: SearchListingType;
    exchange?: SearchExchangeCode[];
  }) {
    void navigate({
      search: buildSearchState({
        query,
        investment_type: investmentType,
        sort_by: sortBy,
        order_by: orderBy,
        equity_sector_slug:
          'equity_sector_slug' in next
            ? next.equity_sector_slug
            : equitySectorSlugs,
        equity_industry_slug:
          'equity_industry_slug' in next
            ? next.equity_industry_slug
            : equityIndustrySlugs,
        market_cap_categories:
          'market_cap_categories' in next
            ? next.market_cap_categories
            : marketCapCategories,
        fund_category_slug:
          'fund_category_slug' in next
            ? next.fund_category_slug
            : fundCategorySlugs,
        listing_type: 'listing_type' in next ? next.listing_type : listingType,
        exchange: 'exchange' in next ? next.exchange : exchangeCodes,
      }),
      resetScroll: false,
      replace: true,
    });
  }

  const handleSearchResultsSortingChange: OnChangeFn<SortingState> = (
    updater,
  ) => {
    if (!investmentType) {
      return;
    }

    const nextSorting = functionalUpdate(updater, searchSorting);

    void navigate({
      search: buildSearchState({
        query,
        investment_type: investmentType,
        equity_sector_slug: equitySectorSlugs,
        equity_industry_slug: equityIndustrySlugs,
        market_cap_categories: marketCapCategories,
        fund_category_slug: fundCategorySlugs,
        listing_type: listingType,
        exchange: exchangeCodes,
        ...getSearchSortParams(investmentType, nextSorting),
      }),
      resetScroll: false,
    });
  };

  function handleResultsTimeframeChange(nextTimeframe: ReturnsTimeframeId) {
    setResultsTimeframe(nextTimeframe);

    if (!investmentType || !returnColumnIds) {
      return;
    }

    const activeSortColumnId = searchSorting[0]?.id;
    const isReturnSortActive =
      activeSortColumnId === returnColumnIds.oneYear ||
      activeSortColumnId === returnColumnIds.fiveYear;

    const nextSorting = isReturnSortActive
      ? syncSortingToReturnsTimeframe(
          searchSorting,
          nextTimeframe,
          returnColumnIds,
        )
      : searchSorting;
    const nextSortParams = getSearchSortParams(investmentType, nextSorting);
    const fallbackSortParams = {
      sort_by: getReturnSortByForTimeframe(nextTimeframe),
      order_by: orderBy ?? 'desc',
    } as const;
    const resolvedSortParams =
      isReturnSortActive && nextSortParams.sort_by
        ? nextSortParams
        : fallbackSortParams;

    if (
      resolvedSortParams.sort_by === sortBy &&
      resolvedSortParams.order_by === orderBy
    ) {
      return;
    }

    void navigate({
      search: buildSearchState({
        query,
        investment_type: investmentType,
        equity_sector_slug: equitySectorSlugs,
        equity_industry_slug: equityIndustrySlugs,
        market_cap_categories: marketCapCategories,
        fund_category_slug: fundCategorySlugs,
        listing_type: listingType,
        exchange: exchangeCodes,
        ...resolvedSortParams,
      }),
      resetScroll: false,
    });
  }

  const searchQuery = useGetSearchResponse(
    buildSearchState({
      query,
      investment_type: investmentType,
      sort_by: sortBy,
      order_by: orderBy,
      equity_sector_slug: equitySectorSlugs,
      equity_industry_slug: equityIndustrySlugs,
      market_cap_categories: marketCapCategories,
      fund_category_slug: fundCategorySlugs,
      listing_type: listingType,
      exchange: exchangeCodes,
      equity_next_pagination_token: search.equity_next_pagination_token,
      equity_prev_pagination_token: search.equity_prev_pagination_token,
      etf_next_pagination_token: search.etf_next_pagination_token,
      etf_prev_pagination_token: search.etf_prev_pagination_token,
      mutual_fund_next_pagination_token:
        search.mutual_fund_next_pagination_token,
      mutual_fund_prev_pagination_token:
        search.mutual_fund_prev_pagination_token,
    }),
    {
      query: {
        placeholderData: keepPreviousData,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  const showEquities = showAll || investmentType === 'equity';
  const showETFs = showAll || investmentType === 'etf';
  const showMutualFunds = showAll || investmentType === 'mutual_fund';
  const isReplacingSearchResults =
    searchQuery.isFetching && searchQuery.isPlaceholderData === true;
  const shouldHidePlaceholderEmptyState = shouldHideEmptyState(
    searchQuery.isFetching,
    searchQuery.isPlaceholderData,
  );

  // Announce pagination to screen readers once the newly requested page has
  // loaded. We wait for fetching to settle (rather than announcing on click) so
  // the message reflects the data that is actually on screen. Declared after
  // `isReplacingSearchResults` because it reads that value.
  useEffect(() => {
    const direction = pendingPaginationDirectionRef.current;

    if (!direction || isReplacingSearchResults) {
      return;
    }

    pendingPaginationDirectionRef.current = null;

    const position = direction === 'next' ? 'next' : 'previous';
    // Re-assign through an empty string first so an identical consecutive
    // message (e.g. next → prev → next landing on the same text) is still seen
    // as a change by the live region and re-announced.
    setPaginationAnnouncement('');
    setPaginationAnnouncement(`Showing ${position} page of results.`);
  }, [isReplacingSearchResults]);

  return (
    <>
      <div role="status" aria-live="polite" className="sr-only">
        {paginationAnnouncement}
      </div>

      <DevSpacer />
      <CategoryNavigation
        productTypes={INTERIOR_NAV_PRODUCT_TYPES}
        sectors={INTERIOR_NAV_SECTORS}
        cta={INTERIOR_NAV_CTA}
        searchQuery={query}
        previewSearchState={{
          investment_type: investmentType,
          sort_by: sortBy,
          order_by: orderBy,
          equity_sector_slug: equitySectorSlugs,
          equity_industry_slug: equityIndustrySlugs,
          market_cap_categories: marketCapCategories,
          fund_category_slug: fundCategorySlugs,
          listing_type: listingType,
          exchange: exchangeCodes,
        }}
        enableSearchPreview
        onSearchSubmit={(nextQuery) => {
          void navigate({
            to: '/search',
            search: buildSearchState({
              query: nextQuery,
              investment_type: investmentType,
              sort_by: sortBy,
              order_by: orderBy,
              equity_sector_slug: equitySectorSlugs,
              equity_industry_slug: equityIndustrySlugs,
              market_cap_categories: marketCapCategories,
              fund_category_slug: fundCategorySlugs,
              listing_type: listingType,
              exchange: exchangeCodes,
            }),
          });
        }}
        isSearchPending={isReplacingSearchResults}
      />

      <ContentLayout>
        <div className="col-content">
          <QueryBoundary query={searchQuery}>
            {(response) => {
              const paginationTokens = getPaginationTokens(
                investmentType,
                response,
              );

              return (
                <div className="flow flow-24 lg:flow-32 pt-12 lg:pt-0">
                  <div className="flex flex-wrap items-start justify-between gap-16">
                    <InvestmentTypeFilter
                      value={investmentType}
                      onChange={handleInvestmentTypeChange}
                    />

                    <ReturnsTimeframeFilter
                      timeframe={resultsTimeframe}
                      onChange={handleResultsTimeframeChange}
                      trackContext="search_results"
                    />
                  </div>

                  <h1 className="sr-only">Search Results for "{query}"</h1>

                  {response.data.top_result_identifier ? (
                    <TopResultCard
                      resultIdentifier={response.data.top_result_identifier}
                      isPending={isReplacingSearchResults}
                    />
                  ) : null}

                  <div className="flex flex-col gap-24">
                    <SectionHeader
                      title="All Results"
                      actionAlignment="center"
                      actions={
                        <a
                          href="https://www.finra.org/finra-data/fixed-income"
                          className="text-static-14 font-semibold uppercase"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-track="search_bond_data_link"
                          data-track-label="Looking for Bond Data?"
                          data-track-context="search_header"
                        >
                          Looking for Bond Data?
                        </a>
                      }
                    >
                      {response.data.total_results_count != null ? (
                        <p className="text-fg-muted text-fluid-base">
                          {response.data.total_results_count} Results
                        </p>
                      ) : null}
                    </SectionHeader>

                    <SearchFacetFilters
                      investmentType={investmentType}
                      equitySectorSlugs={equitySectorSlugs}
                      equityIndustrySlugs={equityIndustrySlugs}
                      marketCapCategories={marketCapCategories}
                      fundCategorySlugs={fundCategorySlugs}
                      listingType={listingType}
                      exchangeCodes={exchangeCodes}
                      onChange={handleFacetChange}
                    />

                    <section
                      className={cn(
                        'flex flex-col gap-24 transition-opacity lg:gap-32',
                        isReplacingSearchResults ? 'opacity-50' : '',
                      )}
                    >
                      {isEmptyResponse(response) ? (
                        shouldHidePlaceholderEmptyState ? (
                          <Spinner label={`Searching for “${query}”`} />
                        ) : (
                          <section className="flow flow-8 py-32">
                            <p className="text-fg-subtle text-fluid-subheader font-bold">
                              We didn’t find any results for “{query}”
                            </p>

                            <p className="text-fg-muted text-static-16">
                              Double check spelling or try a different search
                            </p>
                          </section>
                        )
                      ) : (
                        <>
                          {showEquities && (
                            <SearchResultsSection
                              title="Equities"
                              query={query}
                              investmentType="equity"
                              trackContext="search_results"
                              showTitle={showAll}
                              emptyMessage={
                                shouldHidePlaceholderEmptyState
                                  ? null
                                  : 'No Equities results'
                              }
                              getRowId={equityRowId}
                              columnDefs={equityColumns}
                              data={response.data.equity?.results ?? []}
                              sorting={showAll ? undefined : searchSorting}
                              onSortingChange={
                                showAll
                                  ? undefined
                                  : handleSearchResultsSortingChange
                              }
                              manualSorting={!showAll}
                              displayRowCount={
                                showAll ? SEARCH_RESULT_COUNT : Infinity
                              }
                            />
                          )}

                          {showETFs && (
                            <SearchResultsSection
                              title="ETFs"
                              query={query}
                              investmentType="etf"
                              trackContext="search_results"
                              showTitle={showAll}
                              emptyMessage={
                                shouldHidePlaceholderEmptyState
                                  ? null
                                  : 'No ETF results'
                              }
                              getRowId={etfRowId}
                              columnDefs={etfColumns}
                              data={response.data.etf?.results ?? []}
                              sorting={showAll ? undefined : searchSorting}
                              onSortingChange={
                                showAll
                                  ? undefined
                                  : handleSearchResultsSortingChange
                              }
                              manualSorting={!showAll}
                              displayRowCount={
                                showAll ? SEARCH_RESULT_COUNT : Infinity
                              }
                            />
                          )}

                          {showMutualFunds && (
                            <SearchResultsSection
                              title="Mutual Funds"
                              query={query}
                              investmentType="mutual_fund"
                              trackContext="search_results"
                              showTitle={showAll}
                              emptyMessage={
                                shouldHidePlaceholderEmptyState
                                  ? null
                                  : 'No mutual fund results'
                              }
                              getRowId={mutualFundRowId}
                              columnDefs={mutualFundColumns}
                              data={response.data.mutual_fund?.results ?? []}
                              sorting={showAll ? undefined : searchSorting}
                              onSortingChange={
                                showAll
                                  ? undefined
                                  : handleSearchResultsSortingChange
                              }
                              manualSorting={!showAll}
                              displayRowCount={
                                showAll ? SEARCH_RESULT_COUNT : Infinity
                              }
                            />
                          )}

                          {!showAll &&
                          (paginationTokens.next || paginationTokens.prev) ? (
                            <nav
                              aria-label="Search results pagination"
                              className="flex justify-center gap-12"
                            >
                              <button
                                type="button"
                                className="mdc-btn mdc-btn--md mdc-btn--outline-primary"
                                disabled={
                                  isReplacingSearchResults ||
                                  !paginationTokens.prev
                                }
                                onClick={() => {
                                  handlePaginationChange(
                                    'prev',
                                    paginationTokens.prev,
                                  );
                                }}
                                data-track="search_pagination_prev"
                                data-track-label="Prev"
                                data-track-context="search_pagination"
                              >
                                <Icon
                                  id="chevron-left"
                                  size={16}
                                  aria-hidden="true"
                                />
                                Prev
                              </button>

                              <button
                                type="button"
                                className="mdc-btn mdc-btn--md mdc-btn--outline-primary"
                                disabled={
                                  isReplacingSearchResults ||
                                  !paginationTokens.next
                                }
                                onClick={() => {
                                  handlePaginationChange(
                                    'next',
                                    paginationTokens.next,
                                  );
                                }}
                                data-track="search_pagination_next"
                                data-track-label="Next"
                                data-track-context="search_pagination"
                              >
                                Next
                                <Icon
                                  id="chevron-right"
                                  size={16}
                                  aria-hidden="true"
                                />
                              </button>
                            </nav>
                          ) : null}
                        </>
                      )}
                    </section>
                  </div>
                </div>
              );
            }}
          </QueryBoundary>
        </div>
      </ContentLayout>
    </>
  );
}

function isEmptyResponse(response: getSearchResponseResponseSuccess) {
  return (
    response.data.total_results_count === null ||
    response.data.total_results_count === 0
  );
}

function toSearchListingType(
  value: string | undefined,
): SearchListingType | undefined {
  if (value === 'all' || value === 'listed' || value === 'otc') {
    return value;
  }

  return undefined;
}

function toSearchFilterValues(value: unknown): string[] | undefined {
  if (typeof value === 'string') {
    return [value];
  }

  if (Array.isArray(value)) {
    const values = value.filter(
      (item): item is string => typeof item === 'string',
    );

    return values.length ? values : undefined;
  }

  return undefined;
}

function toSearchMarketCapCategories(
  value: string[] | undefined,
): SearchMarketCapCategory[] | undefined {
  const categories = value?.filter((item): item is SearchMarketCapCategory =>
    isSearchMarketCapCategory(item),
  );

  return categories?.length ? categories : undefined;
}

function toSearchExchangeCodes(
  value: string[] | undefined,
): SearchExchangeCode[] | undefined {
  const exchangeCodes = value?.filter((item): item is SearchExchangeCode =>
    isSearchExchangeCode(item),
  );

  return exchangeCodes?.length ? exchangeCodes : undefined;
}

function shouldHideEmptyState(
  isFetching: boolean,
  isPlaceholderData: boolean | undefined,
) {
  return isFetching && isPlaceholderData === true;
}
